import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Ruler, Minus, Plus, ShieldCheck, Truck, X } from "lucide-react";
import { api, imgUrl, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { toast } from "sonner";

const SIZE_GUIDE = [
  { size: "S", chest: "36-38", length: "27" },
  { size: "M", chest: "38-40", length: "28" },
  { size: "L", chest: "40-42", length: "29" },
  { size: "XL", chest: "42-44", length: "30" },
];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState({ show: false, x: 50, y: 50 });
  const [showGuide, setShowGuide] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setNotFound(false);
    setSize(null);
    setActiveImg(0);
    api
      .get(`/products/${id}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setNotFound(true));
    api.get(`/products/${id}/related`).then(({ data }) => setRelated(data)).catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center min-h-screen" data-testid="product-not-found">
        <p className="font-display text-3xl">Product not found</p>
        <button onClick={() => navigate("/shop")} className="mt-6 border border-white/20 px-6 py-3 font-mono-th text-xs uppercase tracking-[0.2em]">Back to shop</button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-36 pb-20 max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12">
        <div className="aspect-[3/4] bg-[#1A1D24] animate-pulse" />
        <div className="space-y-4">
          <div className="h-8 bg-[#1A1D24] animate-pulse w-2/3" />
          <div className="h-6 bg-[#1A1D24] animate-pulse w-1/3" />
        </div>
      </div>
    );
  }

  const images = product.images?.length ? product.images : [product.image];
  const price = product.discountPrice || product.price;
  const hasDiscount = product.discountPrice && product.discountPrice < product.price;

  const handleAdd = (buyNow = false) => {
    if (!size) {
      toast.error("Please select a size");
      return;
    }
    addItem(product, size, qty);
    if (buyNow) navigate("/checkout");
  };

  return (
    <div className="pt-28 md:pt-32 pb-20 min-h-screen" data-testid="product-detail-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-mono-th text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white mb-8" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Gallery */}
          <div>
            <div
              className="relative aspect-[3/4] overflow-hidden bg-[#1A1D24] cursor-zoom-in"
              onMouseEnter={() => setZoom((z) => ({ ...z, show: true }))}
              onMouseLeave={() => setZoom((z) => ({ ...z, show: false }))}
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setZoom({ show: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
              }}
              data-testid="product-gallery-main"
            >
              <img
                src={imgUrl(images[activeImg])}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-200"
                style={zoom.show ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}
              />
              {product.isSoldOut && (
                <div className="absolute top-4 left-4 border border-rose-500 text-rose-400 font-mono-th text-xs uppercase tracking-[0.2em] px-3 py-1.5">Sold Out</div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {images.map((im, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-24 overflow-hidden border ${activeImg === i ? "border-white" : "border-white/10"}`} data-testid={`product-thumb-${i}`}>
                    <img src={imgUrl(im)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono-th text-xs uppercase tracking-[0.3em] text-neutral-500">{product.category}</p>
            <h1 className="font-display font-black text-3xl md:text-5xl uppercase mt-3 leading-none tracking-tight" data-testid="product-name">{product.name}</h1>

            <div className="flex items-center gap-4 mt-5">
              <span className="font-display font-bold text-2xl" data-testid="product-price">{formatPrice(price)}</span>
              {hasDiscount && <span className="text-neutral-600 line-through text-lg">{formatPrice(product.price)}</span>}
              {hasDiscount && <span className="bg-amber-600 text-white text-[10px] font-mono-th uppercase px-2 py-1">Save {formatPrice(product.price - price)}</span>}
            </div>

            <div className="mt-4">
              {product.isSoldOut ? (
                <span className="font-mono-th text-xs uppercase tracking-[0.2em] text-rose-400" data-testid="product-stock">● Sold Out</span>
              ) : product.stock <= 3 ? (
                <span className="font-mono-th text-xs uppercase tracking-[0.2em] text-amber-400" data-testid="product-stock">● Only {product.stock} left</span>
              ) : (
                <span className="font-mono-th text-xs uppercase tracking-[0.2em] text-emerald-400" data-testid="product-stock">● In Stock</span>
              )}
            </div>

            <div className="flex items-center justify-between mt-8 mb-3">
              <p className="font-mono-th text-[10px] uppercase tracking-[0.25em] text-neutral-500">Select Size</p>
              <button onClick={() => setShowGuide(true)} className="flex items-center gap-1.5 font-mono-th text-[10px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white" data-testid="size-guide-btn">
                <Ruler className="w-3.5 h-3.5" /> Size Guide
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {product.sizes?.map((s) => (
                <button key={s} onClick={() => setSize(s)} disabled={product.isSoldOut} className={`min-w-12 h-12 px-3 border font-mono-th text-sm transition-all disabled:opacity-30 ${size === s ? "bg-white text-black border-white" : "border-white/20 hover:border-white/50"}`} data-testid={`size-${s}`}>
                  {s}
                </button>
              ))}
            </div>

            {!product.isSoldOut && (
              <div className="flex items-center gap-4 mt-8">
                <p className="font-mono-th text-[10px] uppercase tracking-[0.25em] text-neutral-500">Qty</p>
                <div className="flex items-center border border-white/15">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:bg-white/5" data-testid="qty-dec"><Minus className="w-3.5 h-3.5" /></button>
                  <span className="w-10 text-center font-mono-th" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:bg-white/5" data-testid="qty-inc"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button onClick={() => handleAdd(false)} disabled={product.isSoldOut} className="flex-1 bg-white text-black py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-40 hover:bg-neutral-200 transition-colors" data-testid="add-to-cart-btn">
                Add to Bag
              </button>
              <button onClick={() => handleAdd(true)} disabled={product.isSoldOut} className="flex-1 border border-white/25 py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-40 hover:bg-white/5 transition-colors" data-testid="buy-now-btn">
                Buy Now
              </button>
            </div>

            <div className="mt-8 border-t border-white/10 pt-6">
              <p className="font-mono-th text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Description</p>
              <p className="text-neutral-300 text-sm leading-relaxed" data-testid="product-description">{product.description}</p>
              <p className="font-mono-th text-[10px] text-neutral-600 mt-4">SKU: {product.sku}</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="flex items-center gap-3 text-neutral-400 text-xs"><ShieldCheck className="w-5 h-5" /> Authenticity guaranteed</div>
              <div className="flex items-center gap-3 text-neutral-400 text-xs"><Truck className="w-5 h-5" /> Fast pan-India delivery</div>
            </div>
          </motion.div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-24" data-testid="related-section">
            <h2 className="font-display font-black uppercase text-3xl md:text-4xl mb-10">You may also like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        )}
      </div>

      {/* Size guide modal */}
      {showGuide && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setShowGuide(false)} data-testid="size-guide-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="relative glass p-8 w-full max-w-md">
            <button onClick={() => setShowGuide(false)} className="absolute top-4 right-4" data-testid="size-guide-close"><X className="w-5 h-5" /></button>
            <h3 className="font-display font-bold text-xl uppercase mb-6">Size Guide (inches)</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="font-mono-th text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/10">
                  <th className="text-left py-3">Size</th><th className="text-left py-3">Chest</th><th className="text-left py-3">Length</th>
                </tr>
              </thead>
              <tbody>
                {SIZE_GUIDE.map((r) => (
                  <tr key={r.size} className="border-b border-white/5">
                    <td className="py-3 font-semibold">{r.size}</td><td className="py-3 text-neutral-300">{r.chest}"</td><td className="py-3 text-neutral-300">{r.length}"</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      )}
    </div>
  );
}
